import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
} from '@mui/material';
import React from 'react';

import { MobileParameterField } from './MobileParameterField';
import { MobileBlockField } from './MobileBlockField';
import { MobileFileUploadField } from './MobileFileUploadField';
import { MobileFaultSelectionField } from './MobileFaultSelectionField';
import { MobileSignatureField } from './MobileSignatureField';
import { MobileGeoLocationField } from './MobileGeoLocationField';
import { FieldLabel, RequiredMark } from './MobileStatusUpdateDialog.styles';
import { FormFieldType } from '../../../../../../@types/form-builder.type';
import { ServiceObject } from '../../../../../../@types/service.type';
import { SERVICE_PRESENTATION_OPTIONS } from '../../../../../../constants/interaction.constant';

interface FieldData {
  id: number;
  title: string;
  options: any[];
  isRequired: boolean;
  type: string;
  preSelectedData?: any;
  value?: any;
  blockMessage?: string;
}

interface RenderFieldProps {
  interactionId: number;
  field: FieldData;
  formValues: Record<string, any>;
  onFieldChange: (interactionId: number, fieldId: number, value: any) => void;
  onBlockBypass?: (interactionId: number, fieldId: number) => void;
  service?: ServiceObject;
}

export const MobileInteractionFieldRenderer: React.FC<RenderFieldProps> = ({
  interactionId,
  field,
  formValues,
  onFieldChange,
  onBlockBypass,
  service,
}) => {
  const fieldKey = `${interactionId}-${field.id}`;
  const value = formValues[fieldKey] ?? field.value ?? '';
  const fieldType = field.type || '';
  const handleChange = (val: any) => onFieldChange(interactionId, field.id, val);

  switch (fieldType) {
    case FormFieldType.Text:
    case 'Textbox':
    case 'Text':
      return (
        <TextField
          fullWidth
          size="small"
          label={field.title}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          required={field.isRequired}
        />
      );

    case FormFieldType.RichTextbox:
      return (
        <TextField
          fullWidth
          size="small"
          multiline
          rows={3}
          label={field.title}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          required={field.isRequired}
        />
      );

    case FormFieldType.Dropdown:
    case 'Select':
      return (
        <FormControl fullWidth size="small">
          <InputLabel>{field.title}</InputLabel>
          <Select value={value} label={field.title} onChange={(e) => handleChange(e.target.value)}>
            {field.options?.map((opt: any, i: number) => (
              <MenuItem key={opt.id || i} value={opt.value || opt}>
                {opt.title || opt.label || opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );

    case FormFieldType.Radio:
      return (
        <FormControl>
          <FieldLabel>
            {field.title}
            {field.isRequired && <RequiredMark>*</RequiredMark>}
          </FieldLabel>
          <RadioGroup value={value} onChange={(e) => handleChange(e.target.value)}>
            {field.options?.map((opt: any, i: number) => (
              <FormControlLabel
                key={opt.id || i}
                value={opt.value || opt}
                control={<Radio size="small" />}
                label={opt.title || opt.label || opt}
              />
            ))}
          </RadioGroup>
        </FormControl>
      );

    case FormFieldType.Checkbox:
      return (
        <FormControlLabel
          label={field.title}
          control={
            <Checkbox
              checked={!!value}
              onChange={(e) => handleChange(e.target.checked)}
              size="small"
            />
          }
        />
      );

    case FormFieldType.Label:
      return <FieldLabel style={{ fontWeight: 600, fontSize: '14px' }}>{field.title}</FieldLabel>;

    case FormFieldType.SetParameter:
      if (field.preSelectedData) {
        return (
          <MobileParameterField
            preSelectedData={field.preSelectedData}
            value={value}
            onChange={handleChange}
            isRequired={field.isRequired}
          />
        );
      }
      return (
        <FieldLabel style={{ fontStyle: 'italic', opacity: 0.7 }}>
          {field.title}: Will be set automatically
        </FieldLabel>
      );

    case FormFieldType.SetParameterNoUI:
    case FormFieldType.ActionTrigger:
      return null;

    case FormFieldType.GetParameter:
      return (
        <FieldLabel>
          {field.title}: {field.preSelectedData?.parameterValue || value || 'N/A'}
        </FieldLabel>
      );

    case FormFieldType.Block:
      return (
        <MobileBlockField
          blockMessage={field.blockMessage || field.title || 'This action is blocked.'}
          onBypass={() => onBlockBypass?.(interactionId, field.id)}
        />
      );

    case FormFieldType.Date:
      return (
        <TextField
          fullWidth
          size="small"
          type="date"
          label={field.title}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          required={field.isRequired}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      );

    case FormFieldType.EstimatedTimeOfArrival:
      return (
        <TextField
          fullWidth
          size="small"
          type="datetime-local"
          label={field.title || 'Estimated Time of Arrival'}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          required={field.isRequired}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      );

    case FormFieldType.File:
      return (
        <MobileFileUploadField
          title={field.title}
          isRequired={field.isRequired}
          isPhoto={false}
          serviceId={service?.id || 0}
          value={value}
          onChange={handleChange}
        />
      );

    case FormFieldType.Photo:
    case FormFieldType.MerchantDamageTemplate:
      return (
        <MobileFileUploadField
          title={field.title}
          isRequired={field.isRequired}
          isPhoto={true}
          serviceId={service?.id || 0}
          value={value}
          onChange={handleChange}
        />
      );

    case FormFieldType.FaultSelection:
    case FormFieldType.SolutionAction:
      return service ? (
        <MobileFaultSelectionField
          service={service}
          value={value}
          onChange={handleChange}
          isRequired={field.isRequired}
        />
      ) : (
        <FieldLabel>{field.title}: Service context required</FieldLabel>
      );

    case FormFieldType.Signature:
      return (
        <MobileSignatureField
          title={field.title}
          isRequired={field.isRequired}
          value={value}
          onChange={handleChange}
        />
      );

    case FormFieldType.GetGeoLocation:
      return <MobileGeoLocationField title={field.title} value={value} onChange={handleChange} />;

    case FormFieldType.Presentation:
      return (
        <FormControl fullWidth size="small">
          <InputLabel>{field.title || 'Presentation'}</InputLabel>
          <Select
            value={value}
            label={field.title || 'Presentation'}
            onChange={(e) => handleChange(e.target.value)}
          >
            {SERVICE_PRESENTATION_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );

    default:
      return (
        <TextField
          fullWidth
          size="small"
          label={field.title}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => handleChange(e.target.value)}
          required={field.isRequired}
        />
      );
  }
};
